import { useQueryState, parseAsInteger } from "next-usequerystate";
import { useRouter } from "next/router";
import { useState, useMemo, useEffect } from "react";
import { QuizResponse } from "../pages/api/quiz";
import { fetchApi } from "../services/fetch-api";
import { guideTemplates } from "../services/guides";
import { QuizQuestion } from "../services/guides/types";
import { isElementInView } from "../utils/is-element-in-view";
import { logger } from "../utils/logger";
import { normalizeLangCode } from "../utils/normalize-lang-code";
import { scrollToId } from "../utils/scroll-to-id";

export const quizColors = {
  primary: "#779c93",
  secondary: "#c47676",
};

export type QuizStatus = "init" | "ready" | "loading" | "pending" | "error";
export const minHeight = "435px";

const userLangQueryKey = "user-language";
const practiceLangQueryKey = "practice-language";

export function useGuide() {
  const router = useRouter();
  const [selectedUserLanguage, setSelectedUserLanguage] = useQueryState(userLangQueryKey);
  const [selectedPracticeLanguage, setSelectedPracticeLanguage] =
    useQueryState(practiceLangQueryKey);
  const [quizStatus, setQuizStatus] = useState<QuizStatus>("init");
  const [voice, setVoice] = useState<SpeechSynthesisVoice | undefined>();

  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [quizIndex, setQuizIndex] = useQueryState("topic", parseAsInteger);
  const [stepIndex, setStepIndex] = useQueryState("step", parseAsInteger);
  const [showProgress, setShowProgress] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);

  const quizName = useMemo(() => {
    const regex = /guide\/([^?]+)\?/;
    const match = router.asPath.match(regex);

    if (match) {
      return match[1];
    }
  }, [router.asPath]);

  const guideInfo = useMemo(() => {
    if (quizName) {
      const template = guideTemplates.get(quizName);

      if (!template) {
        logger.error("Unknown quiz name");
        return;
      }

      return {
        titleHandle: template.titleHandle,
        longTextHandle: template.longTextHandle,
        shortTextHandle: template.shortTextHandle,
      };
    }
  }, [quizName]);

  const currentStep = useMemo(() => {
    if (quizQuestions && quizIndex !== null && stepIndex !== null) {
      return quizQuestions[quizIndex]?.steps[stepIndex];
    }
  }, [quizQuestions, quizIndex, stepIndex]);

  useEffect(() => {
    const checkVoices = () => {
      const langCode = selectedPracticeLanguage && normalizeLangCode(selectedPracticeLanguage);
      const voiceOptions = window.speechSynthesis.getVoices();
      const voice: SpeechSynthesisVoice | undefined = voiceOptions
        // .sort((a, b) =>
        //   a.name.includes("Google") ? -1 : b.name.includes("Google") ? 1 : 0
        // )
        .find(
          (v) => v.lang === langCode && (v.name.includes("Google") || v.name.includes("Microsoft")),
        );

      setVoice(voice);
    };

    window.speechSynthesis.onvoiceschanged = () => {
      checkVoices();
    };

    checkVoices();
  }, [selectedPracticeLanguage]);

  useEffect(() => {
    if (!quizName || quizStatus !== "init" || !selectedUserLanguage || !selectedPracticeLanguage) {
      return;
    }

    const uLang = normalizeLangCode(selectedUserLanguage);
    const pLang = normalizeLangCode(selectedPracticeLanguage);

    setQuizStatus("loading");

    const timer = setTimeout(() => {
      setQuizStatus("pending");
    }, 2000);

    fetchApi<QuizResponse>(
      `/quiz?userLanguage=${uLang}&practiceLanguage=${pLang}&quizName=${quizName}`,
    )
      .then((response) => {
        if (response.ok && response.quiz.questions) {
          clearTimeout(timer);
          setQuizStatus("ready");
          setQuizQuestions(response.quiz.questions);
        } else if (!response.ok) {
          setQuizStatus("pending");
        } else {
          setQuizStatus("error");
        }
      })
      .catch((err) => {
        logger.error(err);
        setQuizStatus("error");
      });
  }, [quizStatus, router, quizName, selectedUserLanguage, selectedPracticeLanguage]);

  const scrollToTop = () => {
    const quizContainer = document.getElementById("quiz-container");

    if (!isElementInView(quizContainer)) {
      scrollToId("quiz-scroll-target");
    }
  };

  const updateState = (quizIndex: number, stepIndex: number, showProgress: boolean) => {
    if (quizIndex === null || !quizQuestions) {
      alert("Unknown topic");
      return;
    }

    setQuizIndex(quizIndex);
    setStepIndex(stepIndex);
    setShowProgress(showProgress);
    scrollToTop();
  };

  const onReset = () => {
    setQuizIndex(null);
    setStepIndex(null);
    setShowProgress(false);
    setShowCompletion(false);
    scrollToTop();
  };

  const onNextStep = () => {
    if (!quizQuestions) {
      return;
    }

    if (quizIndex === null) {
      logger.info("Starting quiz");
      updateState(0, 0, true);
      return;
    }

    if (showProgress) {
      logger.info("Hide progress and first step");
      updateState(quizIndex, 0, false);
      return;
    }

    const stepsCount = quizQuestions[quizIndex]?.steps.length;
    const nextStepIndex = stepIndex !== null ? stepIndex + 1 : stepIndex;

    if (nextStepIndex && nextStepIndex < stepsCount) {
      updateState(quizIndex, nextStepIndex, false);
      return;
    }

    const nextQuestionIndex = quizIndex + 1;
    const questionCount = quizQuestions.length;

    if (nextStepIndex && nextStepIndex >= stepsCount && nextQuestionIndex < questionCount) {
      updateState(nextQuestionIndex, 0, true);
      return;
    }

    if (nextQuestionIndex && nextStepIndex) {
      setShowCompletion(true);
      scrollToTop();
      return;
    }

    logger.error("No handler for next step - resetting");
    onReset();
  };

  const onPrevStep = () => {
    if (!quizQuestions || quizIndex === null) {
      return;
    }

    if (quizIndex === 0 && stepIndex === 0 && showProgress) {
      logger.info("Hide progress and first step");
      onReset();
      return;
    }

    const prevStepIndex = stepIndex ? stepIndex - 1 : null;

    if (prevStepIndex === null && !showProgress) {
      logger.info("Show progress");
      updateState(quizIndex, 0, true);
      return;
    }

    if (prevStepIndex !== null) {
      updateState(quizIndex, prevStepIndex, false);
      return;
    }

    const prevQuizIndex = quizIndex - 1;

    if (prevQuizIndex >= 0) {
      const prevQuestion = quizQuestions[prevQuizIndex];
      const prevQuestionStepsCount = prevQuestion.steps.length;
      updateState(prevQuizIndex, prevQuestionStepsCount - 1, false);
      return;
    }
  };

  const showContent = quizIndex !== null && quizQuestions && currentStep && !showCompletion;
  const showWelcome = !showContent && !showCompletion;

  return {
    showWelcome,
    quizStatus,
    setQuizStatus,
    selectedUserLanguage,
    setSelectedUserLanguage,
    selectedPracticeLanguage,
    setSelectedPracticeLanguage,
    onNextStep,
    showContent,
    showProgress,
    showCompletion,
    quizQuestions,
    quizIndex: quizIndex || 0,
    currentStep: currentStep || quizQuestions[0]?.steps[0],
    voice,
    guideInfo,
    onReset,
    onPrevStep,
    setStepIndex,
    setQuizIndex,
  };
}
